import { CardListingPosition } from '@hyphengroup/cms-components/cms-blocks/CardListingPosition/CardListingPosition';
import { ApprovalOddsTranslations } from '@hyphengroup/cms-components/components/ApprovalOddsBadge';
import { ApprovalOddsDrawer } from '@hyphengroup/cms-components/components/ApprovalOddsDrawer';
import type { ProductCardApprovalOddsLabels } from '@hyphengroup/cms-components/components/ProductCard/types';
import { SlideUpModalHandle } from '@hyphengroup/cms-components/components/SlideUpModal';
import { AttrValue } from '@hyphengroup/cms-components/types/attrNames';
import { CmsProduct } from '@hyphengroup/cms-components/types/cmsProduct';
import { LoanSearchParams } from '@hyphengroup/cms-components/utils/searchParams/loanSearchParams';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useClientContext } from '../../../../modules/context/client';
import { PersonalLoanFilterWrapper } from '../../components-internal/PersonalLoanFilter/wrapper';
import {
  RECALCULATED_ATTRIBUTE_NAMES,
  RecalculateProductAttributes,
} from '../../helpers/calculateComparisonAttribute';
import { getLoanSearchParamsWithDefaultComponentParam } from '../../helpers/loanSearchParams';
import { useLoanSearchParams } from '../../helpers/useLoanSearchParams';
import { pushDataLayer } from '../../pushDataLayer';
import { ComponentProductMetadata, MetadataProductCard } from '../../types';
import { CardListingWrapperProps } from './types';
import {
  useApprovalOdds,
  useApprovalOddsRetrieval,
} from '$/(cms)/_modules/approvalOdds';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import { getClientTranslations } from '$/modules/i18n/client.index';

type CalculationResult = Parameters<typeof RecalculateProductAttributes>[1];

/**
 * Checks if a product card has a valid product block.
 */
const hasProduct = (p: MetadataProductCard): boolean =>
  Boolean(p?.productBlock?.product);

/**
 * Extracts product IDs from product cards that have valid products.
 */
const getProductIds = (products: MetadataProductCard[]): string[] =>
  products
    .filter(hasProduct)
    .map((p) => p.productBlock?.product?.title)
    .filter((id): id is string => Boolean(id));

/**
 *  Extracts products from product cards that have valid products.
 */
const getProducts = (products: MetadataProductCard[]): CmsProduct[] =>
  products
    .filter(hasProduct)
    .map((p) => p.productBlock?.product)
    .filter((product): product is CmsProduct => Boolean(product?.title));

/**
 * Applies calculation results to product cards by matching indices.
 * Only products with valid product blocks receive calculation results.
 * Returns new array with updated attributes (does not mutate originals).
 */
const applyCalculationResults = (
  productCards: MetadataProductCard[],
  results: ReadonlyArray<CalculationResult> | undefined,
): MetadataProductCard[] => {
  if (!results?.length) {
    return productCards;
  }

  const validProducts = productCards.filter(hasProduct);

  return productCards.map((prod) => {
    const productIndex = validProducts.indexOf(prod);
    if (productIndex === -1) {
      return prod;
    }

    const result = results[productIndex];
    const attributes = prod.productBlock?.product?.attributes;
    if (!attributes) {
      return prod;
    }
    // A missing entry is recalculated too, blanking the attributes: the
    // authored "0.00" it would otherwise keep parses to 0 and ranks the product
    // as the cheapest, while the comparison table ranks it as missing.

    return {
      ...prod,
      productBlock: {
        ...prod.productBlock,
        displayApprovalOdds: prod.productBlock?.displayApprovalOdds,
        product: {
          ...prod.productBlock?.product,
          attributes: RecalculateProductAttributes(attributes, result),
        },
      },
    };
  });
};

/**
 * Checks if all required attributes (from attributes_list) have values in the product.
 *
 * A calculated attribute is exempt from the value check: it is authored empty and
 * filled per loan amount and tenure, so an empty one means the product is not
 * priceable at the current inputs. The comparison table keeps that product with
 * blank cells (`ComparisonTableWrapper.tsx:50-60`), and dropping the card here
 * instead would show the two components different product sets (req 6 / AC 5c).
 */
const hasAllRequiredAttributes = (product: MetadataProductCard): boolean => {
  const requiredAttrs = product.productBlock?.attributes_list ?? [];
  const productAttrs = product.productBlock?.product?.attributes ?? [];

  return requiredAttrs.every((attr) => {
    const attrName = attr.attribute?.name;
    if (!attrName) return true; // Skip invalid config

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const baseAttr: any = productAttrs.find((a) => a.name?.name === attrName);
    if (!baseAttr) return false;
    if (RECALCULATED_ATTRIBUTE_NAMES.includes(attrName as AttrValue))
      return true;

    return !!(baseAttr.value || baseAttr.values);
  });
};

export const CardListingWrapperPersonalLoan: React.FC<
  CardListingWrapperProps
> = (props) => {
  const { metadata, vertical, tableConfig } = props;
  const initialProductCards = useMemo(
    () => metadata?.product_card ?? [],
    [metadata?.product_card],
  );
  const oddsDrawerRef = React.useRef<SlideUpModalHandle>(null);
  const context = useClientContext();
  const { brand, langPrefix } = context;
  const approvalOddsEnabled =
    brand === 'moneyhero' &&
    initialProductCards.some((p) => p.productBlock?.displayApprovalOdds);
  const products = useMemo(
    () => (approvalOddsEnabled ? getProducts(initialProductCards) : []),
    [approvalOddsEnabled, initialProductCards],
  );
  const { data: approvalOddsData, sendConsent } = useApprovalOdds(
    products,
    approvalOddsEnabled,
    langPrefix,
  );

  const approvalOddsLabels = useMemo<
    ProductCardApprovalOddsLabels | undefined
  >(() => {
    if (!approvalOddsEnabled) return undefined;
    const t = getClientTranslations(ApprovalOddsTranslations, context);
    return {
      sectionTitle: t.approval_odds,
      tooltip: t.approval_odds_tooltip,
      high: t.approval_odds_high,
      medium: t.approval_odds_medium,
      low: t.approval_odds_low,
      unlock: t.approval_odds_unlock,
    };
  }, [approvalOddsEnabled, context]);

  const { pageData, isPreview, publicCmsConfig } = usePageDataContext();

  const { onRetrieveApprovalOdds, oddsConsentModal } = useApprovalOddsRetrieval(
    {
      approvalOddsData,
      sendConsent,
      langPrefix,
      chcBaseUrl: publicCmsConfig.chcBaseUrl,
    },
  );

  const [calculateError, setCalculateError] = useState(
    !!pageData.calculatedPersonalLoans?.hasError,
  );
  const [productCards, setProductCards] = useState(() =>
    applyCalculationResults(
      initialProductCards,
      pageData.calculatedPersonalLoans?.data,
    ),
  );

  const initialLoanSearchParams = useMemo(
    () =>
      getLoanSearchParamsWithDefaultComponentParam(brand, undefined, {
        amount: tableConfig?.default_loan_amount?.toString(),
        tenure: tableConfig?.default_loan_tenure?.toString(),
      }),
    [brand, tableConfig?.default_loan_amount, tableConfig?.default_loan_tenure],
  );

  const { loanSearchParams: changedLoanSearchParams } = useLoanSearchParams(
    initialLoanSearchParams as LoanSearchParams,
  );

  const visibleProductCards = useMemo(() => {
    const existing = productCards.filter(hasProduct);
    return calculateError
      ? existing
      : existing.filter(hasAllRequiredAttributes);
  }, [productCards, calculateError]);

  const handleFilterChange = async ({ tenure, amount }: LoanSearchParams) => {
    const productIds = getProductIds(initialProductCards);

    try {
      const results = await feApiFrontClient.calculatePersonalLoans({
        productIds,
        tenure: +tenure,
        amount: +amount,
        isDraft: isPreview,
      });
      setCalculateError(false);
      if (results) {
        setProductCards(applyCalculationResults(initialProductCards, results));
      }
    } catch {
      setCalculateError(true);
    }
  };

  const metadataToSend: ComponentProductMetadata | undefined = metadata
    ? { ...metadata, product_card: visibleProductCards }
    : undefined;

  const handleOpenOddsDrawer = () => oddsDrawerRef.current?.open();

  return (
    <PersonalLoanFilterWrapper
      wrapperClassName={classNames({
        'hidden md:block': !props.cardPosition.visible_mobile,
      })}
      onlyListen
      vertical={vertical}
      onFinishedEnterFilter={handleFilterChange}
      defaultParams={initialLoanSearchParams}
      max_loan_amount={tableConfig?.max_loan_amount}
      min_loan_amount={tableConfig?.min_loan_amount}
      max_loan_tenure={tableConfig?.max_loan_tenure}
      min_loan_tenure={tableConfig?.min_loan_tenure}
    >
      <CardListingPosition
        {...props}
        metadata={metadataToSend}
        calculateError={calculateError}
        skeletonThreshold={0}
        travelQuotes={undefined}
        travelPremiumsData={undefined}
        initialLoanSearchParams={changedLoanSearchParams}
        plFilterConfig={pageData.plFilterConfig}
        approvalOddsData={approvalOddsEnabled ? approvalOddsData : null}
        approvalOddsLabels={approvalOddsLabels}
        onApprovalOddsUnlockClick={
          approvalOddsEnabled ? onRetrieveApprovalOdds : undefined
        }
        onApprovalOddsRefreshClick={handleOpenOddsDrawer}
      />
      {approvalOddsEnabled && (
        <ApprovalOddsDrawer
          slideUpModalRef={oddsDrawerRef}
          lastUpdated={approvalOddsData?.lastUpdated ?? undefined}
          isOutdated={approvalOddsData?.userState !== 'fresh'}
          onClickCTA={onRetrieveApprovalOdds}
          pushDataLayer={pushDataLayer}
        />
      )}
      {approvalOddsEnabled ? oddsConsentModal : null}
    </PersonalLoanFilterWrapper>
  );
};
